/**
 * The typed API client — the ONLY place in the app that calls fetch.
 *
 * ESLint enforces that (`no-restricted-syntax` on `fetch`, exempted for this folder).
 * The legacy app had three copies of a `_json` helper, two bespoke fetch wrappers and
 * about sixty inline URL templates; every inconsistency in error handling, auth and
 * pagination followed from that.
 */

import { getRuntimeConfig } from '@/lib/config/runtime-config';
import { ApiError, toApiError } from './errors';
import { getToken, notifyUnauthorized } from './session';

/** The backend is two separate FastAPI apps on two origins sharing one JWT. */
export type ServiceName = 'ingestion' | 'enrichment';

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

/**
 * Both services accept and echo X-Correlation-ID, generating one when absent.
 * Sending our own is the only way to tie a user action to the backend's JSON logs,
 * so every request carries one.
 */
function correlationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly query?: Readonly<Record<string, QueryValue>>;
  /** JSON body. Mutually exclusive with `formData`. */
  readonly body?: unknown;
  /** Multipart body, for CSV/XLSX upload routes. Sets no Content-Type — the browser does. */
  readonly formData?: FormData;
  /** Always pass the one TanStack Query provides, so requests cancel on unmount. */
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
}

function baseUrl(service: ServiceName): string {
  const config = getRuntimeConfig();
  return service === 'ingestion' ? config.ingestionServiceUrl : config.enrichmentServiceUrl;
}

/** Serializes query params, repeating the key for arrays and dropping null/undefined. */
export function buildQuery(query: Readonly<Record<string, QueryValue>> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, String(entry));
    } else {
      params.append(key, String(value));
    }
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.headers.get('content-length') === '0') return null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }
  const text = await response.text().catch(() => '');
  return text === '' ? null : text;
}

/**
 * Performs a request and returns the parsed body.
 *
 * Throws `ApiError` for every failure — including network failures, as status 0 — so
 * callers have exactly one error type to handle. Note that a 409 is a normal state for
 * this API's job triggers; check `error.isAlreadyRunning` rather than treating it as
 * a failure.
 */
export async function request<T = unknown>(
  service: ServiceName,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', query, body, formData, signal, headers } = options;

  if (body !== undefined && formData !== undefined) {
    throw new Error('request() takes either `body` or `formData`, not both.');
  }

  const url = `${baseUrl(service)}${path}${buildQuery(query)}`;

  const requestHeaders: Record<string, string> = {
    accept: 'application/json',
    'X-Correlation-ID': correlationId(),
    ...headers,
  };
  const token = getToken();
  const sentToken = token !== null;
  if (token) requestHeaders['Authorization'] = `Bearer ${token}`;

  let payload: BodyInit | undefined;
  if (formData !== undefined) {
    payload = formData;
  } else if (body !== undefined) {
    requestHeaders['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: requestHeaders,
      ...(payload === undefined ? {} : { body: payload }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // An aborted request is not an error the user should ever see.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      'Could not reach the server. Check your connection and try again.',
      0,
      { payload: cause },
    );
  }

  const parsed = await parseBody(response);

  if (!response.ok) {
    // A 401 on a request that carried a token means the session is gone. A 401 with no
    // token is simply bad credentials, and must not tear down the session.
    if (response.status === 401 && sentToken) notifyUnauthorized();
    throw toApiError(response.status, parsed);
  }

  return parsed as T;
}

/**
 * Downloads a file, returning the blob and the server-suggested filename.
 *
 * Only ingestion exposes Content-Disposition; enrichment exposes no response headers,
 * so `filename` falls back to the caller's default there.
 */
export async function download(
  service: ServiceName,
  path: string,
  options: Omit<RequestOptions, 'body' | 'formData'> & { fallbackFilename: string },
): Promise<{ blob: Blob; filename: string }> {
  const { method = 'GET', query, signal, headers, fallbackFilename } = options;
  const url = `${baseUrl(service)}${path}${buildQuery(query)}`;

  const requestHeaders: Record<string, string> = {
    'X-Correlation-ID': correlationId(),
    ...headers,
  };
  const token = getToken();
  const sentToken = token !== null;
  if (token) requestHeaders['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: requestHeaders,
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0, {
      payload: cause,
    });
  }

  if (!response.ok) {
    if (response.status === 401 && sentToken) notifyUnauthorized();
    throw toApiError(response.status, await parseBody(response));
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const encoded = match?.[1];
  let filename = fallbackFilename;
  if (encoded) {
    try {
      filename = decodeURIComponent(encoded);
    } catch {
      filename = encoded;
    }
  }

  return { blob: await response.blob(), filename };
}

/** Convenience wrappers. */
export const api = {
  get: <T = unknown>(service: ServiceName, path: string, options?: Omit<RequestOptions, 'method' | 'body' | 'formData'>) =>
    request<T>(service, path, { ...options, method: 'GET' }),

  post: <T = unknown>(service: ServiceName, path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(service, path, { ...options, method: 'POST' }),

  put: <T = unknown>(service: ServiceName, path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(service, path, { ...options, method: 'PUT' }),

  patch: <T = unknown>(service: ServiceName, path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(service, path, { ...options, method: 'PATCH' }),

  delete: <T = unknown>(service: ServiceName, path: string, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(service, path, { ...options, method: 'DELETE' }),

  download,
} as const;
